// src/Pages/ModuleRecords/WorkforceInstitutionalAddRecord.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import WorkforceCreateModal from '../../component/Workforce/WorkforceCreateModal';
import { useAuth } from '../../context/AuthContext';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import {
  livestockActorsApi,
  workforceDeploymentsApi,
  cooperativesApi,
} from '../../api/workforceApi';

const SUB_MODULES = ['actors', 'deployment', 'coop'];
const ROUTES = {
  actors: '/workforce-institutional/actors',
  deployment: '/workforce-institutional/deployment',
  coop: '/workforce-institutional/coop',
};
const API_BY_SUBMODULE = {
  actors: livestockActorsApi,
  deployment: workforceDeploymentsApi,
  coop: cooperativesApi,
};

export default function WorkforceInstitutionalAddRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';

  const activeSubModule = useMemo(() => {
    if (user?.roleId === 'R05') return 'actors';
    const requested = String(searchParams.get('subModule') ?? '').trim();
    return SUB_MODULES.includes(requested) ? requested : 'actors';
  }, [searchParams, user?.roleId]);

  const mode = uuid ? (viewOnly ? 'view' : 'edit') : 'create';

  // ── Edit/View mode: load the existing record ────────────────────────────
  // Prefer the already-normalized row handed over via navigation state (the
  // exact object the table already fetched from the live API) — this is
  // known-complete. Fall back to a fresh live GET only when the page is
  // opened without that state (e.g. a direct link or a refresh).
  const [row, setRow] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(Boolean(uuid));
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!uuid) return;
    const seedRow = location.state?.row;
    if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) {
      setRow(seedRow);
      setLoadingRecord(false);
      return;
    }
    const api = API_BY_SUBMODULE[activeSubModule] ?? livestockActorsApi;
    setLoadingRecord(true);
    setLoadError('');
    api.get(uuid)
      .then((r) => setRow(r))
      .catch((e) => setLoadError(e?.response?.data?.message ?? e.message ?? 'Failed to load record.'))
      .finally(() => setLoadingRecord(false));
  }, [uuid, activeSubModule]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    navigate(ROUTES[activeSubModule] ?? ROUTES.actors, { replace: true });
  };

  const canEdit = roleId !== 'R02' && roleId !== 'R06';

  return (
    <div className="min-h-screen bg-[#f7f7f7] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        {loadingRecord && (
          <div className="mb-4 rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm text-gray-500 shadow-sm">
            Loading record…
          </div>
        )}
        {loadError && (
          <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
            {loadError}
          </div>
        )}
        {(mode === 'create' || row) && (
          <RegistryFormScope moduleKey="workforce-institutional" submoduleKey={activeSubModule}>
            <>
              <RegistryCustomFields moduleKey="workforce-institutional" submoduleKey={activeSubModule} />
              <WorkforceCreateModal
                isOpen
                inline
                mode={mode}
                row={mode === 'create' ? null : row}
                onClose={handleClose}
                onCreate={handleClose}
                onEdit={() => navigate(`/workforce-institutional/edit/${uuid}?subModule=${activeSubModule}`, { state: { row } })}
                canEdit={canEdit}
                defaultSubModule={activeSubModule}
              />
            </>
          </RegistryFormScope>
        )}
      </div>
    </div>
  );
}
